import type { BillingCycle } from '../lib/pricing';
import { t, type MessageKey } from '../lib/i18n';
import { cn } from '../lib/cn';

/**
 * Selector segmentado del ciclo de facturación (1 mes / 6 meses / 1 año).
 *
 * Reutilizable — misma pieza en la landing y en facturación para garantizar que el
 * ciclo mostrado sea consistente en todo el producto. Colores 100% desde tokens.
 * Accesible: `role="radiogroup"` + cada opción `role="radio"` con `aria-checked`.
 */
const OPTIONS: { value: BillingCycle; labelKey: MessageKey }[] = [
  { value: 'monthly', labelKey: 'pricing.cycle.monthly' },
  { value: 'semiannual', labelKey: 'pricing.cycle.semiannual' },
  { value: 'yearly', labelKey: 'pricing.cycle.yearly' },
];

export function BillingCycleToggle({
  value,
  onChange,
  className,
}: {
  value: BillingCycle;
  onChange: (cycle: BillingCycle) => void;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={t('pricing.cycle.label')}
      className={cn(
        'inline-flex overflow-hidden rounded-token border border-line text-xs font-medium',
        className,
      )}
    >
      {OPTIONS.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'px-3 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset',
              active ? 'bg-brand text-brand-ink' : 'text-muted hover:text-ink',
            )}
          >
            {t(opt.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
